// src/services/domusAiService.js
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const BOT_ENDPOINT = 'https://us-central1-gestionpagosfracc.cloudfunctions.net/botDomusConsultaHttp';
const BOT_API_KEY = 'DOMUS_BOT_SECURE_2026_KEY';

/**
 * Envía un mensaje al backend de Asistencia Inteligente de Domus Habita (Web y Móvil)
 * @param {Object} params
 * @param {string} params.mensaje
 * @param {Object} params.usuario
 * @param {Array} [params.historial]
 * @returns {Promise<{ ok: boolean, respuesta: string, error?: string }>}
 */
export const enviarMensajeChatWeb = async ({ mensaje, usuario, historial = [] }) => {
  try {
    const response = await fetch(BOT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-bot-api-key': BOT_API_KEY
      },
      body: JSON.stringify({
        accion: 'chat_web',
        mensaje,
        usuario,
        historial
      })
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Error HTTP ${response.status}`);
    }

    const data = await response.json();
    return {
      ok: true,
      respuesta: data.respuesta || 'No se pudo generar una respuesta.',
      sugerencias: Array.isArray(data.sugerencias) ? data.sugerencias : [],
      logId: data.logId || null
    };
  } catch (error) {
    console.error('Error comunicando con Asistente IA Domus:', error);
    return {
      ok: false,
      error: error.message,
      respuesta: 'Lo siento, ocurrió una interrupción de conexión con el Asistente IA. Por favor intenta enviar tu mensaje nuevamente.',
      sugerencias: []
    };
  }
};

/**
 * Registra la calificación (👍 / 👎) del usuario sobre una respuesta del Asistente IA
 * Prioriza guardado directo en Firestore con sincronización en tiempo real y fallback a Cloud Functions.
 */
export const calificarRespuestaChat = async ({
  mensajeId,
  pregunta,
  respuesta,
  calificacion,
  comentario = '',
  usuarioUid = null,
  nombreUsuario = '',
  casa = '',
  rol = '',
  fraccionamientoId = null,
  fraccionamientoNombre = ''
}) => {
  const docPayload = {
    mensajeId: mensajeId || `msg_${Date.now()}`,
    pregunta: String(pregunta || ''),
    respuesta: String(respuesta || ''),
    calificacion: calificacion === 'buena' ? 'buena' : 'mala',
    comentario: String(comentario || '').trim(),
    usuarioUid: usuarioUid || null,
    nombreUsuario: nombreUsuario || 'Anónimo',
    casa: casa || 'N/A',
    rol: rol || 'residente',
    fraccionamientoId: fraccionamientoId || null,
    fraccionamientoNombre: fraccionamientoNombre || 'Domus Habita',
    fecha: serverTimestamp()
  };

  try {
    // 1. Guardado directo y prioritario en Firestore Client SDK (<100ms)
    const docRef = await addDoc(collection(db, 'chatbot_calificaciones'), docPayload);
    return { exito: true, feedbackId: docRef.id };
  } catch (directErr) {
    console.warn('Aviso: Guardado directo en Firestore falló, intentando vía Cloud Function:', directErr);
    // 2. Fallback resiliente a Cloud Functions
    try {
      const response = await fetch(BOT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-bot-api-key': BOT_API_KEY
        },
        body: JSON.stringify({
          accion: 'calificar_respuesta',
          ...docPayload,
          fecha: new Date().toISOString()
        })
      });

      if (!response.ok) {
        return { exito: false };
      }

      return await response.json();
    } catch (err) {
      console.error('Error registrando calificación de chat vía Cloud Function:', err);
      return { exito: false, error: err.message };
    }
  }
};

/**
 * Registra un comprobante de pago subido por un residente en el backend y notifica a la administración
 * @param {Object} params
 * @param {string} params.fraccionamientoId
 * @param {string} params.casa
 * @param {string} params.residente
 * @param {string} params.archivoUrl
 * @param {number} [params.monto]
 * @param {string} [params.notas]
 * @param {string} [params.usuarioUid]
 * @returns {Promise<{ exito: boolean, comprobanteId?: string, error?: string }>}
 */
export const registrarComprobantePago = async ({
  fraccionamientoId,
  casa,
  residente,
  archivoUrl,
  monto = 0,
  notas = '',
  usuarioUid = null
}) => {
  try {
    const response = await fetch(BOT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-bot-api-key': BOT_API_KEY
      },
      body: JSON.stringify({
        accion: 'subir_comprobante',
        fraccionamientoId,
        casa,
        residente,
        archivoUrl,
        monto,
        notas,
        usuarioUid
      })
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Error HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error registrando comprobante:', error);
    return { exito: false, error: error.message };
  }
};
